/**
 * Shared vocabulary for the whole system.
 *
 * The hard rule of this codebase: the simulation advances on a fixed integer
 * tick, and every number that influences the outcome must be derived from
 * (seed, config, action log). Nothing here may read a clock or a random source
 * at runtime, otherwise a replay would not be reproducible.
 */

export const TICKS_PER_SECOND = 10;

/** Arena is a square world measured in world units. */
export interface MatchConfig {
  /** Random seed. Same seed + same actions => byte-identical match. */
  seed: number;
  /** Arena is [0, size] on both axes. */
  arenaSize: number;
  /** Match length in ticks. */
  durationTicks: number;
  /** Agent ids, in slot order. Slot 0 is "red", slot 1 is "purple". */
  agentIds: readonly [string, string];
  /** Cricket stats. */
  stats: CricketStats;
  /** How often each agent produces a decision (decisions are aligned to a grid). */
  decisionIntervalTicks: number;
  /**
   * When true, both slots share decision phase 0 so they think on the same ticks.
   *
   * Used for live-model matches: the world holds until every due model has
   * answered, so two AIs reason about the same board and neither is re-polled
   * while the other is still thinking.
   */
  alignedDecisions?: boolean;
  /**
   * Whether killing the opponent immediately wins the match.
   *
   * Defaults to `false`. The ranking score is crystal score + remaining HP, so
   * surviving and dealing damage both matter; an instant elimination win would
   * still short-circuit that tradeoff. Set this to `true` for a future
   * "survival" mode (docs/002.md battle type ②).
   */
  eliminationEndsMatch?: boolean;
}

export interface CricketStats {
  maxHp: number;
  /** World units per tick. */
  speed: number;
  /** Sight radius. Resources outside this radius are invisible to the agent. */
  visionRadius: number;
  /** Damage of a melee attack. */
  attackDamage: number;
  /** Max distance at which ATTACK connects. */
  attackRange: number;
  /** Cooldown between attacks, in ticks. */
  attackCooldownTicks: number;
  /** Ticks spent channelling a COLLECT before the resource is banked. */
  collectTicks: number;
  /** Max distance at which COLLECT can target a resource. */
  collectRange: number;
  /** Ticks of forced displacement from a successful attack. */
  knockbackTicks: number;
  /** World-units multiplier applied each knockback tick (on top of speed). */
  knockbackSpeedMul: number;
}

export type AgentId = string;

export type Vec2 = readonly [number, number];

/* ------------------------------------------------------------------ */
/* Actions: what an agent may do.                                      */
/* ------------------------------------------------------------------ */

export interface MoveAction {
  readonly action: "MOVE";
  readonly target: Vec2;
  /** Written to the timeline as a human-readable "why". Never affects physics. */
  readonly reason?: string;
}

export interface ScoutAction {
  readonly action: "SCOUT";
  readonly target: Vec2;
  readonly reason?: string;
}

export interface CollectAction {
  readonly action: "COLLECT";
  readonly resourceId?: string;
  readonly reason?: string;
}

export interface AttackAction {
  readonly action: "ATTACK";
  readonly targetId?: AgentId;
  readonly reason?: string;
}

export interface DefendAction {
  readonly action: "DEFEND";
  /** Ticks to hold the defensive stance, clamped to [1, 30]. */
  readonly durationTicks?: number;
  readonly reason?: string;
}

export interface WaitAction {
  readonly action: "WAIT";
  readonly reason?: string;
}

/** Sprint away from the opponent (or toward the nearest edge if unseen). */
export interface FleeAction {
  readonly action: "FLEE";
  readonly reason?: string;
}

/** Spend crystal score to buy a combat item into inventory. */
export interface ExchangeAction {
  readonly action: "EXCHANGE";
  readonly item: ItemKind;
  readonly reason?: string;
}

/** Activate a combat item from inventory. */
export interface UseAction {
  readonly action: "USE";
  readonly item: ItemKind;
  readonly reason?: string;
}

export type Action =
  | MoveAction
  | ScoutAction
  | CollectAction
  | AttackAction
  | DefendAction
  | WaitAction
  | FleeAction
  | ExchangeAction
  | UseAction;

export type ActionType = Action["action"];

/** Combat items that can sit in inventory (map pickups or crystal exchange). */
export type ItemKind = "bomb" | "blade";

/* ------------------------------------------------------------------ */
/* Observation: what an agent may see.                                 */
/* ------------------------------------------------------------------ */

export type ResourceKind =
  | "crystal"
  | "energy"
  | "shield"
  | "trap"
  | "bomb"
  | "blade";

/**
 * World-truth feedback for the previous decision.
 *
 * Agents only observe on decision ticks. Without this, a failed COLLECT /
 * ATTACK looks identical to a successful one in the next observation — models
 * then spam the same verb and freeze. The detail string is English on purpose:
 * it is fed into the LLM prompt as ground truth, not spectator chrome.
 */
export interface ActionOutcome {
  readonly tick: number;
  readonly action: ActionType;
  readonly ok: boolean;
  /** Stable code for heuristics / tests (e.g. "out-of-range", "banked"). */
  readonly code: string;
  /** Short English sentence for the agent prompt. */
  readonly detail: string;
  /** Optional resource / item / target the outcome refers to. */
  readonly resourceId?: string;
  readonly distance?: number;
}

export interface SelfView {
  readonly id: AgentId;
  readonly position: Vec2;
  readonly hp: number;
  readonly maxHp: number;
  /** Crystal / pickup score banked so far (not including remaining HP). */
  readonly score: number;
  /** Ranking total used at the whistle: score + remaining HP. */
  readonly totalScore: number;
  readonly collecting: boolean;
  readonly defending: boolean;
  readonly fleeing: boolean;
  readonly bladeActive: boolean;
  readonly attackReadyAt: number;
  readonly knockedBackUntil: number;
  readonly totalCollected: number;
  readonly failedCollections: number;
  /** Bombs currently held (map pickup or EXCHANGE). */
  readonly bombs: number;
  /** Spinning-blade charges currently held. */
  readonly blades: number;
}

export interface OpponentView {
  /** Always present: agents need it to address an ATTACK. */
  readonly id: AgentId;
  /** Undefined unless the opponent is currently inside our vision radius. */
  readonly visible: boolean;
  readonly position?: Vec2;
  readonly hp?: number;
  readonly score?: number;
  readonly distance?: number;
}

export interface ResourceView {
  readonly id: string;
  readonly kind: ResourceKind;
  readonly position: Vec2;
  readonly value: number;
  /** WORLD TRUTH: does this agent know it is a trap (from prior scouting)? */
  readonly identifiedAsTrap: boolean;
  /** Within reach of a COLLECT right now. */
  readonly inRange: boolean;
  readonly distance: number;
}

export interface Observation {
  readonly tick: number;
  readonly timeRemainingTicks: number;
  /** World bounds, so agents can reason about space without hardcoding it. */
  readonly arenaSize: number;
  readonly self: SelfView;
  readonly opponent: OpponentView;
  /** Only resources inside vision radius are included. No cheating. */
  readonly resources: readonly ResourceView[];
  /** Resources this agent has personally identified as traps. */
  readonly knownTraps: readonly string[];
  /** Resources sitting just outside vision that were previously sighted. */
  readonly lastKnownResources: readonly ResourceView[];
  /** True once the opponent has been spotted at least once this match. */
  readonly hasSeenOpponent: boolean;
  /** Last observed opponent position, retained from memory (may be stale). */
  readonly lastOpponentPosition?: Vec2;
  /**
   * Result of the most recent submitted action (or its channel completion /
   * interrupt). Absent only before the first action of the match.
   */
  readonly lastActionOutcome?: ActionOutcome;
}

/* ------------------------------------------------------------------ */
/* Events: the match log. Replay = events; Timeline = narrative.       */
/* ------------------------------------------------------------------ */

export type EventType =
  | "match:start"
  | "match:tick"
  | "match:end"
  | "agent:decision"
  | "agent:moved"
  | "agent:scouted"
  | "agent:attack"
  | "agent:attack:miss"
  | "agent:hit"
  | "agent:defend"
  | "agent:collect:start"
  | "agent:collect:complete"
  | "agent:collect:miss"
  | "agent:collect:interrupted"
  | "agent:tracked"
  | "resource:spawn"
  | "resource:collected"
  | "resource:identified"
  | "trap:triggered"
  | "trap:detected"
  | "agent:knockback"
  | "agent:eliminated"
  | "agent:flee"
  | "agent:exchange"
  | "agent:use"
  | "item:bomb"
  | "item:blade"
  | "score";

export interface SimEvent {
  readonly seq: number;
  /** Sim tick at which this happened. */
  readonly tick: number;
  readonly type: EventType;
  /** Present when the event is attributable to one cricket. */
  readonly agentId?: AgentId;
  readonly data: Record<string, unknown>;
}

export type EventRecorder = (ev: SimEvent) => void;

/* ------------------------------------------------------------------ */
/* Match results                                                       */
/* ------------------------------------------------------------------ */

export interface AgentResult {
  readonly id: string;
  readonly score: number;
  readonly hp: number;
  /** Final ranking total: score + remaining HP. */
  readonly totalScore: number;
  readonly resourcesCollected: number;
  readonly alive: boolean;
  readonly eliminated: boolean;
  /** Raw behavioural counters, used to build personality cards later. */
  readonly behavioural: BehaviouralCounters;
}

export interface BehaviouralCounters {
  decisions: number;
  moves: number;
  attacksAttempted: number;
  attacksLanded: number;
  collectionsAttempted: number;
  collectionsCompleted: number;
  collectionsFailed: number;
  trapsSteppedOn: number;
  damageDealt: number;
  damageTaken: number;
  distanceTravelled: number;
  /** Max distance ever travelled from spawn, a proxy for exploration. */
  maxExplorationDistance: number;
  /**
   * SCOUT actions issued. Distinct from exploration distance: running across the
   * map and actively spending ticks to gather intel are different behaviours, and
   * only the latter should earn a cricket the title of scout.
   */
  scoutActions: number;
  /**
   * Traps identified (SCOUT pulse or proximity sense). Used for the scouting
   * trait so a cricket that never presses SCOUT but still notices mines is not
   * scored as blind.
   */
  trapsDetected: number;
  /** Average speed toward the nearest resource at decision time. */
  resourceFocusSum: number;
  resourceFocusSamples: number;
  /** Ticks spent retreating from a visible opponent. */
  retreatTicks: number;
}

export interface MatchResult {
  readonly matchId: string;
  readonly seed: number;
  readonly durationTicks: number;
  readonly winner: string | "draw";
  readonly winnerReason: "score" | "elimination" | "timeout" | "draw";
  readonly agents: readonly AgentResult[];
  readonly highlights: readonly Highlight[];
  readonly eventCount: number;
}

export interface Highlight {
  readonly tick: number;
  readonly type: string;
  readonly agentId?: AgentId;
  readonly text: string;
  readonly score: number;
}

export type AgentFactory = (observation: Observation) => Action | Promise<Action>;

/**
 * A single turn of conversation with a model provider.
 *
 * This lives in the protocol package rather than the agents package because it
 * is part of the shared vocabulary: the engine's replay and analysis tooling
 * both record and inspect model traffic.
 */
export interface ChatMessage {
  readonly role: "system" | "user" | "assistant";
  readonly content: string;
}

export interface Agent {
  readonly id: string;
  /** Display name shown in the UI, e.g. "THE GAMBLER". */
  readonly persona?: string;
  /** Which model powers this cricket, shown one layer down in the UI. */
  readonly model?: string;
  decide(observation: Observation): Action | Promise<Action>;
}

export function formatTick(tick: number): string {
  const totalSeconds = Math.floor(tick / TICKS_PER_SECOND);
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export function distance(a: Vec2, b: Vec2): number {
  const dx = a[0] - b[0];
  const dy = a[1] - b[1];
  return Math.sqrt(dx * dx + dy * dy);
}
